import { formatPhone } from "@vaulted/shared";
import { cx } from "./ui";

/** Text or email, at a glance: an icon and a colour each (texts pink like a chat, emails blue like mail). */
export function ChannelChip({ channel, to, direction }: { channel: "sms" | "email"; to?: string | null; direction?: "in" | "out" }) {
  const email = channel === "email";
  const who = to ? (email ? to : formatPhone(to)) : null;
  return (
    <span className={cx("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold", email ? "bg-[#e0ecff] text-[#1d4ed8]" : "bg-brand/15 text-brand-deep")}>
      <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        {email ? <path d="M3 6h18v12H3zM3 7l9 6 9-6" /> : <path d="M4 5h16v11H8l-4 4V5z" />}
      </svg>
      {email ? "Email" : "Text"}
      {who && <span className="font-normal">{direction === "in" ? ` from ${who}` : ` to ${who}`}</span>}
    </span>
  );
}

type Groupable = { id: string; channel: "sms" | "email"; direction: "in" | "out"; templateKey: string | null; at: string };

/**
 * The same automatic message sent by text and by email at once (a confirmation, a reminder) is one
 * entry with both versions, not two that look like a duplicate. Order is kept.
 */
export function pairTextAndEmail<T extends Groupable>(list: T[], sameTo: (a: T, b: T) => boolean = () => true): (T | { pair: [T, T] })[] {
  const out: (T | { pair: [T, T] })[] = [];
  const used = new Set<string>();
  for (let i = 0; i < list.length; i++) {
    const m = list[i]!;
    if (used.has(m.id)) continue;
    const partner =
      m.direction === "out" && m.templateKey
        ? list.find(
            (o, j) =>
              j > i &&
              j <= i + 3 &&
              !used.has(o.id) &&
              o.direction === "out" &&
              o.templateKey === m.templateKey &&
              o.channel !== m.channel &&
              sameTo(m, o) &&
              Math.abs(Date.parse(o.at) - Date.parse(m.at)) < 2 * 60_000,
          )
        : undefined;
    if (partner) {
      used.add(partner.id);
      const text = m.channel === "sms" ? m : partner;
      out.push({ pair: [text, text === m ? partner : m] });
    } else out.push(m);
  }
  return out;
}

export const isPair = <T,>(x: T | { pair: [T, T] }): x is { pair: [T, T] } => typeof x === "object" && x !== null && "pair" in x;

/** Which way it went: from the client (received) or to them (sent). */
export function DirectionTag({ direction }: { direction: "in" | "out" }) {
  const inbound = direction === "in";
  return (
    <span className={cx("inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold", inbound ? "bg-ok-wash text-ok" : "bg-mist text-ink-soft")}>
      <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
        {inbound ? <path d="M12 4v15M6 13l6 6 6-6" /> : <path d="M12 20V5M6 11l6-6 6 6" />}
      </svg>
      {inbound ? "From client" : "To client"}
    </span>
  );
}
